export type DiffLine = { type: "same" | "add" | "remove"; text: string };


export interface DiffOptions { ignoreWhitespace?: boolean; ignoreCase?: boolean }

/** The text two lines are compared by; the original text is still what is shown. */
export function compareKey(s: string, o: DiffOptions = {}): string {
  let k = s;
  if (o.ignoreWhitespace) k = k.replace(/\s+/g, " ").trim();
  if (o.ignoreCase) k = k.toLowerCase();
  return k;
}

export function diffLines(a: string[], b: string[], o: DiffOptions = {}): DiffLine[] {
  const ka = a.map((s) => compareKey(s, o));
  const kb = b.map((s) => compareKey(s, o));
  const n = a.length;
  const m = b.length;
  // LCS dynamic-programming table
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = ka[i] === kb[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const result: DiffLine[] = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (ka[i] === kb[j]) {
      result.push({ type: "same", text: a[i] });
      i++; j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      result.push({ type: "remove", text: a[i] });
      i++;
    } else {
      result.push({ type: "add", text: b[j] });
      j++;
    }
  }
  while (i < n) { result.push({ type: "remove", text: a[i] }); i++; }
  while (j < m) { result.push({ type: "add", text: b[j] }); j++; }
  return result;
}

export type Seg = { text: string; changed: boolean };
/** Word-level diff of one removed line against its replacement. */
export function wordDiff(a: string, b: string, o: DiffOptions = {}): { a: Seg[]; b: Seg[] } {
  const ta = a.split(/(\s+)/).filter(Boolean);
  const tb = b.split(/(\s+)/).filter(Boolean);
  const ops = diffLines(ta, tb, o);
  const sa: Seg[] = [];
  const sb: Seg[] = [];
  for (const o of ops) {
    if (o.type === "same") { sa.push({ text: o.text, changed: false }); sb.push({ text: o.text, changed: false }); }
    else if (o.type === "remove") sa.push({ text: o.text, changed: true });
    else sb.push({ text: o.text, changed: true });
  }
  return { a: sa, b: sb };
}

/** For each remove/add run of equal pairing, attach word segments by position. */
export function withWordSegments(diff: DiffLine[], o: DiffOptions = {}): (DiffLine & { segs?: Seg[] })[] {
  const out: (DiffLine & { segs?: Seg[] })[] = diff.map(d => ({ ...d }));
  let i = 0;
  while (i < out.length) {
    if (out[i].type !== "remove") { i++; continue; }
    let r = i;
    while (r < out.length && out[r].type === "remove") r++;
    let a = r;
    while (a < out.length && out[a].type === "add") a++;
    const pairs = Math.min(r - i, a - r);
    for (let k = 0; k < pairs; k++) {
      const w = wordDiff(out[i + k].text, out[r + k].text, o);
      out[i + k].segs = w.a;
      out[r + k].segs = w.b;
    }
    i = a;
  }
  return out;
}

/** Splits on LF or CRLF so Windows and Unix line endings compare equal. */
export const splitLines = (s: string) => s.split(/\r?\n/);

export function isIdentical(diff: DiffLine[]): boolean {
  return diff.length > 0 && diff.every((d) => d.type === "same");
}

export type SideCell = { type: "same" | "add" | "remove"; text: string; segs?: Seg[] } | null;
export type SideRow = { left: SideCell; right: SideCell };

/** Pairs removed and added lines into rows for a side-by-side view. A run of removals is aligned with the run of additions that follows it. */
export function toSideBySide(diff: (DiffLine & { segs?: Seg[] })[]): SideRow[] {
  const rows: SideRow[] = [];
  let i = 0;
  while (i < diff.length) {
    const d = diff[i];
    if (d.type === "same") { rows.push({ left: d, right: d }); i++; continue; }
    const rem: typeof diff = [], add: typeof diff = [];
    while (i < diff.length && diff[i].type === "remove") rem.push(diff[i++]);
    while (i < diff.length && diff[i].type === "add") add.push(diff[i++]);
    for (let k = 0; k < Math.max(rem.length, add.length); k++) rows.push({ left: rem[k] ?? null, right: add[k] ?? null });
  }
  return rows;
}
